import { useFetcher } from '@remix-run/react';
import { useAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { Eraser } from 'lucide-react';
import { nanoid } from 'nanoid';
import { useEffect } from 'react';

import { Button } from '../ui/button';
import ChatBottombar from './chat-bottombar';
import ChatList from './chat-list';

const WELCOME_MESSAGE = {
    isMine: false,
    message: `Hello 👋 How can I help you? Type your question below 👇`,
};

const messageHistory = atomWithStorage(
    'pw.message_history',
    [WELCOME_MESSAGE],
    undefined,
    { getOnInit: true },
);

const userChatId = atomWithStorage('pw.chat_id', '', undefined, {
    getOnInit: true,
});

export function ChatWidget({
    onBeforeSubmit,
    onAfterSubmit,
}: {
    onBeforeSubmit: () => boolean;
    onAfterSubmit: () => void;
}) {
    const [items, setItems] = useAtom(messageHistory);
    const [chatId, setChatId] = useAtom(userChatId);
    const clearHistory = () => {
        setItems([WELCOME_MESSAGE]);
        setChatId(nanoid());
    };
    const fetcher = useFetcher();
    const isFetching = fetcher.state !== 'idle';

    useEffect(() => {
        if (!chatId) {
            setChatId(nanoid());
        }
    }, [chatId, setChatId]);

    useEffect(() => {
        if (!fetcher.data || fetcher.state !== 'idle') {
            return;
        }

        const payload = fetcher.data;

        setItems((prevAssets) => [
            ...prevAssets,
            {
                isEmpty: payload.isEmptyResult,
                error: payload.error,
                message: payload.response,
                isMine: false,
            },
        ]);
    }, [fetcher.data, fetcher.state]);

    const addMessage = (msg) => {
        if (!onBeforeSubmit()) {
            return;
        }

        const MAX_HISTORY_LENGTH = 200;
        fetcher.submit(
            { q: msg, chatId },
            { method: 'POST', action: '/ai/bot' },
        );

        setItems([
            ...items.slice(-1 * MAX_HISTORY_LENGTH),
            { message: msg, isMine: true },
        ]);

        onAfterSubmit();
    };

    return (
        <div className='flex flex-col w-full h-full'>
            <div className='w-full flex flex-col flex-grow overflow-y-auto px-4 bg-slate-50'>
                <ChatList messages={items} isFetching={isFetching} />
            </div>
            <div>
                <ChatBottombar
                    sendMessage={addMessage}
                    waitForResponse={isFetching}
                />
                <div className='flex flex-row justify-between items-center gap-x-2 px-1 pb-1 text-muted-foreground'>
                    <Button
                        variant={'ghost'}
                        className='text-xs'
                        onClick={clearHistory}>
                        <Eraser className='w-4 h-4 mr-1' />
                        Clear history
                    </Button>
                </div>
            </div>
        </div>
    );
}
